import { EventEmitter, Injectable } from '@angular/core';
import { I18nServiceConfig } from './models-external';
import { Lang, CIMODE, TRANSLATION_NAMESPACE, LangNamespaceLookup, ResourcesRecord } from './models-internal';
import { ResourceMap } from './resources';
import { validateLanguageCodes, normalizeLanguageCodes, applyConfigDefaults } from './functions';

/** Log line prefix. */
export const PREFIX = '[i18n]';

/**
 * The I18n service.
 *
 * Some terminology:
 * - A *dictionary* is a map of translations. `en/translation.json` is a dictionary.
 * - Each dictionary belongs to a *namespace*. The “translation” namespace is the default namespace.
 * - A *resource* is a set of dictionaries, assigned to each namespace.
 */

@Injectable()
export class I18nService {
  /**
   * The set of resources available.
   */
  private resources: ResourceMap;

  /**
   * The fallback language, used if the current language doesn't contain the phrase.
   *
   * Behind the fallback language is a further fallback to cimode.
   */
  private fallbackLang: Lang;

  /**
   * The current language used by the application.
   */
  private currentLang: Lang;

  /**
   * When the language changes, this emits the new language.
   */
  public readonly languageChanged = new EventEmitter<string>();

  /**
   * Some update has happened in the i18n service's records, and components should redraw.
   */
  public readonly updated = new EventEmitter<string>();

  /**
   * Debug mode produces more verbose debug-friendly logging.
   */
  private debug = false;

  public initialise(_config: I18nServiceConfig) {
    const config = applyConfigDefaults(_config);
    this.debug = config.debug;
    this.fallbackLang = config.fallbackLang;
    this.currentLang = config.initialLang;

    validateLanguageCodes(config);

    this.resources = new ResourceMap(config.resources);

    if (this.debug) {
      console.debug(PREFIX, 'Finished compiling translations');
      console.debug(PREFIX, 'Available languages:', this.getAvailableLanguages().join(", "));
      console.debug(PREFIX, 'Starting in language:', this.currentLang);
    }
  }

  public getCurrentLanguage() {
    return this.currentLang;
  }

  public getAvailableLanguages() {
    return this.resources.getAvailableLanguages();
  }

  public getDictionary(options: LangNamespaceLookup = {}) {
    const lang = options.lang ?? this.currentLang;
    const namespace = options.namespace ?? TRANSLATION_NAMESPACE;
    const dictionary = this.resources.getDictionary({ lang, namespace });
    return dictionary?.getAsJson();
  }

  /**
   * Get the translation for a given key.
   *
   * This will look first in the current language, then the fallback language.
   * If no language has this key, the key itself is returned.
   *
   * @param key The key to look up.
   */
  public getPhrase(key: string, options: LangNamespaceLookup = {}) {
    const phrase = this.fetchPhrase(key, options);
    const parsed = this.parsePhrase(key, phrase, options);
    return parsed;
  }

  /**
   * Load new resources during runtime.
   *
   * In the event of any conflict with existing dictionaries, the new dictionaries will be used instead of the old ones.
   */
  public loadResources(newResources: ResourcesRecord) {
    this.resources.load(newResources);
    this.updated.emit();
  }

  /**
   * Change the current language.
   *
   * @param lang The new language to change to
   */
  public changeLanguage(lang: Lang) {
    if (this.debug) {
      console.debug("Language changed from", this.currentLang, "to", lang);
    }
    this.currentLang = lang;
    this.languageChanged.emit(lang);
    this.updated.emit();
  }

  /**
   * Parse a phrase, including interpolation.
   *
   * @param origin The key from which this parse request first originated. Used to detect circular loops.
   * @param phrase The phrase being parsed.
   *
   * @returns The pharsed phrase
   */
  private parsePhrase(origin: string, phrase: string, options: LangNamespaceLookup) {
    const interpolatedKeyRegex = /\$t\(([\w.]+)\)/; // $t(words)
    while (true) {
      const match = phrase.match(interpolatedKeyRegex);
      if (match === null) break;
      const [ fulltext, key ] = match;
      if (key === origin) {
        console.error(`Circular loop detected in phrase "${origin}", detected from "${key}". Giving up parsing.`);
        return key;
      }
      const otherPhrase = this.fetchPhrase(key, options);
      const parsed = this.parsePhrase(origin, otherPhrase, options);
      phrase = phrase.replace(fulltext, parsed);
    }
    return phrase;
  }

  /**
   * Fetch a phrase from translations.
   *
   * @param key The key of the phrase to fetch.
   */
  private fetchPhrase(key: string, options: LangNamespaceLookup) {
    const namespace = options.namespace ?? TRANSLATION_NAMESPACE;
    const languages = normalizeLanguageCodes([options.lang, this.currentLang, this.fallbackLang]);

    for (const lang of languages) {
      if (lang === CIMODE) {
        return key;
      }

      const phrase = this.resources.getPhrase({ key, lang, namespace });
      if (typeof phrase !== 'undefined') {
        return phrase;
      }
    }

    if (this.debug) {
      console.error(PREFIX, 'Translation not found for key:', key);
    }
    return key;
  }
}
